// The slim list's Message History — `extension-alert-candidates-messages-tooltip.tpl.html` and
// its contacts twin `extension-alert-client-contacts-messages-tooltip.tpl.html`, which are the
// same markup with the controller field renamed. Opened from a row's envelope
// (`openCandidateMessagesTooltip` / `openClientContactMessagesTooltip`).
//
// ⚠️ IT IS NOT ANCHORED TO THE ROW. Upstream hangs the `uib-tooltip-template` off an EMPTY div
// between the filters bar and the selection bar (`extension-alert-candidates-slim.tpl.html:73`),
// and `_extension-alert-candidates-messages-tooltip.scss` then pins the popup at
// `position: fixed; top: 62px; left: -4px` with `!important` — so whichever row was clicked,
// the history opens in the same place at the top of the frame. Positioning it beside the
// envelope would be a redesign.
//
// ⚠️ AND ANY CLICK CLOSES IT, INSIDE THE POPUP INCLUDED. `on-click-outside` is on that empty
// anchor, and the popup is appended to `<body>`, so nothing is ever "inside" — measured on the
// live tenant, a click on the "Message History" heading closes it exactly as the ✕ does.
import { useCallback, useRef, useState, type ReactElement } from 'react'
import { getMessageDataForEntity, type EntityMessageData, type MessageEntity } from '@woo-ext/api'
import { displayDate } from '../common/displayDate.ts'
import { UibTooltipPopup } from '../common/UibTooltip.tsx'
import { useDismissOnOutsideClick } from '../common/useDismissOnOutsideClick.ts'

/**
 * The section title of one history line — the three `ng-show` spans, of which exactly one
 * shows: no `sendDate` and no `failureReason` is still queued; no `sendDate` WITH one never
 * will be (upstream woo@9d1ab15f0a); otherwise the send date, in `getDisplayDate`'s format.
 *
 * ⚠️ ONLY `ATTACHMENT_MISSING` HAS ITS OWN WORDS. Every other reason — `SEND_FAILED` and
 * anything the server adds later — reads "could not be sent", which is the template's
 * `=== 'ATTACHMENT_MISSING' ? … : …`, not a lookup with a fallback for unknowns.
 */
export function messageHistoryTitle(data: EntityMessageData): string {
  if (data.sendDate === undefined || data.sendDate === null || data.sendDate === 0) {
    if (data.failureReason === undefined || data.failureReason === null) return 'Pending'
    return `Failed: ${data.failureReason === 'ATTACHMENT_MISSING' ? 'attachment not found' : 'could not be sent'}`
  }
  return displayDate(data.sendDate) ?? ''
}

export interface MessageHistoryTooltipProps {
  /** `isCandidateMessagesDataTooltipOpen` / `isClientContactMessagesDataTooltipOpen`. */
  open: boolean
  /** `currentCandidateMessagesData` — `undefined` while the request is in flight (the spinner). */
  messages: readonly EntityMessageData[] | undefined
  /** `closeCandidateMessagesTooltip()` — the ✕, and any click anywhere. */
  onClose: () => void
}

/**
 * The anchor div AND, while open, the popup. Render it where upstream's anchor sits: between
 * the filters bar and `.candidate-selection-container`.
 */
export function MessageHistoryTooltip({ open, messages, onClose }: MessageHistoryTooltipProps): ReactElement {
  const anchor = useRef<HTMLDivElement>(null)
  useDismissOnOutsideClick({ open, onDismiss: onClose, within: [] })
  return (
    <div ref={anchor} className="extension-candidate-messages-tooltip slim-filters-and-selection-section">
      {!open ? null : (
        <UibTooltipPopup host={anchor} placement="bottom" popupClass="extension-candidate-messages-wrap">
          <div className="extension-candidate-messages-container">
            {messages === undefined ? (
              <div>
                <i className="fa fa-spinner fa-spin fa-lg" />
              </div>
            ) : (
              <div>
                <div className="extension-candidate-messages-header">
                  <div className="extension-candidate-messages-header-container">Message History</div>
                  <div className="extension-candidate-messages-x-icon">
                    <i className="far fa-times fa-lg" onClick={onClose} />
                  </div>
                </div>
                <div className="extension-candidate-messages-content">
                  <div className="extension-candidate-messages-content-container nicer-scroller four-px-scroll">
                    {messages.map((data, index) => (
                      // eslint-disable-next-line react/no-array-index-key -- `ng-repeat` over a one-time list; the lines have no id.
                      <div key={index} className="extension-candidate-messages-content-item">
                        <div className="extension-candidate-messages-section-title">
                          <span>{messageHistoryTitle(data)}</span>
                        </div>
                        <div className="extension-candidate-messages-section-text-container">
                          Sent by {data.userName}
                          {data.alertName === undefined || data.alertName === null || data.alertName === '' ? null : (
                            <span> for {data.alertName}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </UibTooltipPopup>
      )}
    </div>
  )
}

export interface MessageHistoryState extends MessageHistoryTooltipProps {
  /** `open…MessagesTooltip(entity, $event)` — the row's envelope, past its own early return. */
  openFor: (entityId: number) => void
}

/**
 * The controller half: `openCandidateMessagesTooltip` / `openClientContactMessagesTooltip`
 * and the matching close. Spread the result onto `MessageHistoryTooltip` and hand `openFor`
 * to each row's `onOpenMessages`.
 *
 * ⚠️ THE DATA IS CLEARED BEFORE THE REQUEST, SO A SECOND ROW NEVER SHOWS THE FIRST ROW'S
 * HISTORY while its own loads — upstream nulls `currentCandidateMessagesData` first, which is
 * what puts the spinner up. And a request that fails leaves the spinner, as upstream's
 * unhandled `await` does; only a response for the row asked about LAST is shown, which
 * upstream does not guard and a fast second click would otherwise expose.
 */
export function useMessageHistory(entity: MessageEntity): MessageHistoryState {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<readonly EntityMessageData[] | undefined>(undefined)
  const latest = useRef(0)
  const openFor = useCallback(
    (entityId: number) => {
      const request = ++latest.current
      setMessages(undefined)
      setOpen(true)
      getMessageDataForEntity(entity, entityId).then(
        (loaded) => {
          if (request === latest.current) setMessages(loaded)
        },
        () => undefined,
      )
    },
    [entity],
  )
  // `close…MessagesTooltip()` — the data is left in place, as upstream leaves it.
  const onClose = useCallback(() => {
    setOpen(false)
  }, [])
  return { open, messages, onClose, openFor }
}
